import type { FC, ReactElement } from "react"

import filter from "lodash-es/filter"
import isEmpty from "lodash-es/isEmpty"
import reject from "lodash-es/reject"
import { DateTime } from "luxon"

import {
    Button,
    ESessionsCounterColor,
    ETypographyAlignment,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyWeight,
    SessionsCounter,
    Typography,
    mapSessionExtraPartyModelsToSessionInfoExtraParties,
    renderSessionInfoHeaderAvatar
} from "@/3514/components"
import { withDynamicHeightGuard } from "@/3514/guards"
import {
    ESessionScheduleModalMode,
    ESessionScheduleModalOrigin,
    type TSessionScheduleModalProps
} from "@/3514/modals/session-schedule"
import { EModalId, type IModal, type TModalDispatcher, useModalDispatcher, useModalSelector } from "@/3514/store/modal"
import type { ICoachingSessionModel } from "@/3514/types"
import { getTimeZoneAbbreviation, withPressHandler } from "@/3514/utils"
import { type IUseTranslation, type TTFunction, useTranslation } from "@/hooks"
import { type TEmptyCallback } from "@/shared/types/functions"

import styles from "../../styles"
import type { TCoachingSessionsScheduleAvailableModalProps as TModalProps } from "../../types"

const {
    modal: {
        scheduleAvailable: { body: classes }
    }
} = styles

function _renderSessionsBlock(params: {
    sessions: ICoachingSessionModel[]
    t: TTFunction
    isExtraParty: boolean
    containerAction: TEmptyCallback
}): ReactElement {
    return (
        <div
            role="tab"
            tabIndex={0}
            onClick={withPressHandler(params.containerAction)}
            onKeyDown={withPressHandler(params.containerAction)}
            className={classes.session.container({ withPaddings: !params.isExtraParty })}
        >
            <Typography
                text={params.t(
                    params.isExtraParty
                        ? "entities.sessions.modal.scheduleAvailable.3wayBlock.label"
                        : "entities.sessions.modal.scheduleAvailable.1on1Block.label"
                )}
                family={ETypographyFamily.DmSerif}
                weight={ETypographyWeight.Medium}
            />

            <div>
                <div className={classes.session.wrapper()}>
                    {renderSessionInfoHeaderAvatar(params.sessions[0].modality, {
                        isGuest: params.isExtraParty,
                        size: 40
                    })()}

                    <div className={classes.session.nameWrapper()}>
                        <Typography
                            text={`${params.sessions[0].sessionName} (${params.sessions.length})`}
                            size={ETypographySize.Small}
                            className={classes.session.name()}
                        />
                        <Typography
                            text={
                                params.isExtraParty
                                    ? getTimeZoneAbbreviation()
                                    : `${params.sessions[0].sessionDuration} ${params.t("common.min")}`
                            }
                            size={ETypographySize.Small}
                            color={ETypographyColor.Dark}
                        />
                    </div>

                    {((): ReactElement =>
                        DateTime.fromISO(params.sessions[0].schedulingWindowEnd).isValid && (
                            <Typography
                                text={params.t("entities.sessions.modal.scheduleAvailable.expirationLabel", {
                                    date: DateTime.fromISO(params.sessions[0].schedulingWindowEnd).toFormat("MMM d")
                                })}
                                size={ETypographySize.Small}
                                color={ETypographyColor.Gray23}
                                className={classes.session.expiration()}
                                isItalic
                            />
                        ))()}
                </div>

                <SessionsCounter
                    dataLength={params.sessions.length}
                    maxLinesCount={3}
                    color={ESessionsCounterColor.Blue}
                />
            </div>
        </div>
    )
}

const Body: FC = (): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const {
        props: { sessions, sessionsQueryKey, user, participantId }
    }: IModal<TModalProps> = useModalSelector<TModalProps>(EModalId.ScheduleAvailableSessions)

    const modalDispatcher: TModalDispatcher = useModalDispatcher()

    function handleModalHide(): void {
        modalDispatcher.hideModal(EModalId.ScheduleAvailableSessions)
    }

    function handleSessionAction(s: ICoachingSessionModel): TEmptyCallback {
        return (): void => {
            modalDispatcher.hideModal(EModalId.ScheduleAvailableSessions)

            modalDispatcher.showModal<TSessionScheduleModalProps>(EModalId.ScheduleSession, {
                isExtraPartySession:
                    s.isExtraPartySession &&
                    !isEmpty(mapSessionExtraPartyModelsToSessionInfoExtraParties(s.extraParties)),
                sessionId: s.sessionId,
                queryKey: sessionsQueryKey,
                user: { id: user.id, firstName: user.firstName, lastName: user.lastName, photo: user.photo },
                participantId,
                origin: ESessionScheduleModalOrigin.Sessions,
                mode: ESessionScheduleModalMode.Schedule,
                withCloseOnEscapeClick: true
            })
        }
    }

    const normalSessions: ICoachingSessionModel[] = reject<ICoachingSessionModel>(sessions, {
        isExtraPartySession: true
    })
    const threeWaySessions: ICoachingSessionModel[] = filter<ICoachingSessionModel>(sessions, {
        isExtraPartySession: true
    })

    return (
        <div className={classes.container()}>
            <Typography
                text={t("entities.sessions.modal.scheduleAvailable.description")}
                alignment={ETypographyAlignment.Center}
                withHyphens
                isPretty
            />

            <div role="tablist" className={classes.session.holder()}>
                {isEmpty(normalSessions)
                    ? null
                    : _renderSessionsBlock({
                          sessions: normalSessions,
                          t,
                          isExtraParty: false,
                          containerAction: handleSessionAction(normalSessions[0])
                      })}

                {isEmpty(threeWaySessions)
                    ? null
                    : _renderSessionsBlock({
                          sessions: threeWaySessions,
                          t,
                          isExtraParty: true,
                          containerAction: handleSessionAction(threeWaySessions[0])
                      })}
            </div>

            <Button
                isCentered
                className={classes.button()}
                onClick={withPressHandler(handleModalHide)}
                onKeyDown={withPressHandler(handleModalHide)}
            >
                {t("common.close")}
            </Button>
        </div>
    )
}

Body.displayName = "CoachingSessionsScheduleAvailableModalBody"

export default withDynamicHeightGuard(Body)
